import type { Auth } from "firebase/auth";
import { connectAuthEmulator } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import { connectFirestoreEmulator } from "firebase/firestore";
import type { Functions } from "firebase/functions";
import { connectFunctionsEmulator } from "firebase/functions";

/** Explicit local demo Auth only; never accept emulator configuration on hosted builds. */
export function readLocalAdminFirebaseEmulator({
  projectId,
  host,
  development,
  pageHostname,
}: {
  projectId: unknown;
  host: unknown;
  development: boolean;
  pageHostname: string | undefined;
}) {
  if (
    host === undefined &&
    (typeof projectId !== "string" || !projectId.startsWith("demo-"))
  )
    return null;
  const port = typeof host === "string" ? Number(host.split(":")[1]) : NaN;
  if (
    !development ||
    !["localhost", "127.0.0.1", "[::1]"].includes(pageHostname ?? "") ||
    typeof projectId !== "string" ||
    !/^demo-[a-z0-9](?:[a-z0-9-]{0,23}[a-z0-9])?$/u.test(projectId) ||
    typeof host !== "string" ||
    !/^127\.0\.0\.1:[1-9][0-9]{0,4}$/u.test(host) ||
    port > 65535
  ) {
    throw new Error(
      "Local Admin Firebase emulator requires a local development page, demo project and literal loopback endpoint.",
    );
  }
  return { projectId, hostname: "127.0.0.1", port, origin: `http://${host}` };
}

const connections = new WeakMap<
  Auth,
  { origin: string; firestore: Firestore; functions: Functions }
>();

const failedConnections = new WeakSet<Auth>();

/** Both SDK entry paths must bind before readiness/observation, regardless of import order. */
export function connectLocalAdminFirebaseEmulator(
  clients: { auth: Auth; firestore: Firestore; functions: Functions },
  emulator: ReturnType<typeof readLocalAdminFirebaseEmulator>,
): void {
  if (failedConnections.has(clients.auth)) {
    throw new Error(
      "Local Admin Firebase emulator initialization failed; restart the owned runtime.",
    );
  }
  const existing = connections.get(clients.auth);
  if (!emulator) {
    if (existing)
      throw new Error(
        "Local Admin Firebase emulator changed after initialization.",
      );
    return;
  }
  if (clients.auth.app.options.projectId !== emulator.projectId) {
    throw new Error(
      "Local Admin Firebase app does not match the demo project.",
    );
  }
  if (existing) {
    if (
      existing.origin !== emulator.origin ||
      existing.firestore !== clients.firestore ||
      existing.functions !== clients.functions
    ) {
      throw new Error(
        "Local Admin Firebase emulator changed after initialization.",
      );
    }
    return;
  }
  try {
    connectAuthEmulator(clients.auth, emulator.origin, {
      disableWarnings: true,
    });
    // This Auth-only lane does not simulate Firestore/Functions. Unexpected calls
    // must fail on the owned listener, never fall through to a cloud service.
    connectFirestoreEmulator(
      clients.firestore,
      emulator.hostname,
      emulator.port,
    );
    connectFunctionsEmulator(
      clients.functions,
      emulator.hostname,
      emulator.port,
    );
  } catch (error) {
    failedConnections.add(clients.auth);
    throw error;
  }
  connections.set(clients.auth, {
    origin: emulator.origin,
    firestore: clients.firestore,
    functions: clients.functions,
  });
}
